import PropTypes from 'prop-types';
import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useMemo
} from 'react';
import { login, logout, resetPassword } from 'store/slices/authSlice';
import { connect } from 'react-redux';
import { AuthHelper } from '../utils/helpers/auth.helper';
import UserService from '../services/user.service';
import axios from 'axios';
import { buildResiAccessSummary } from 'utils/resi/access/buildResiAccessSummary';
import UserInvitesService from 'services/user-invite.service';
import { watchPendingInvites } from 'services/pending-invites-watch';

const AuthContext = createContext(null);

export const AuthProvider = ({ authUser, children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [orgs, setOrgs] = useState([]);
    const [userProperties, setUserProperties] = useState([]);
    const [currentAccessKeys, setCurrentAccessKeys] = useState([]);
    const [accessKeysLoading, setAccessKeysLoading] = useState(false);

    const [userInvites, setUserInvites] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchCurrentUser = useCallback(async () => {
        try {
            const [userData, orgsData, userPropertiesData, accessKeysData] =
                await Promise.all([
                    UserService.getCurrentUser(),
                    UserService.getUserOrgs(),
                    UserService.getUserProperties(),
                    UserService.getCurrentAccessKeys()
                ]);

            setCurrentUser(userData);
            setOrgs(orgsData || []);
            setUserProperties(userPropertiesData || []);
            setCurrentAccessKeys(accessKeysData || []);
        } catch (error) {
            console.error('Error fetching current user:', error);

            setCurrentUser(null);
            setOrgs([]);
            setUserProperties([]);
            setCurrentAccessKeys([]);
            setUserInvites([]);
        } finally {
            setLoading(false);
        }
    }, []);
    const refreshAccessKeys = useCallback(async () => {
        try {
            setAccessKeysLoading(true);

            const accessKeysData = await UserService.getCurrentAccessKeys();

            setCurrentAccessKeys(accessKeysData || []);

            return accessKeysData || [];
        } catch (error) {
            console.error('Error fetching access keys:', error);
            setCurrentAccessKeys([]);
            throw error;
        } finally {
            setAccessKeysLoading(false);
        }
    }, []);
    // One-time load through the API. The list normally comes from the live listener below;
    // this is the fallback when that cannot run. Keeps the current list when the request
    // fails: an empty list would hide invites the user still has to answer.
    const refreshInvites = useCallback(async () => {
        try {
            const invitesData = await UserInvitesService.getUserInvites();

            setUserInvites(invitesData || []);
        } catch (error) {
            console.error('Error fetching invites:', error);
        }
    }, []);
    const removeInvite = useCallback(inviteId => {
        setUserInvites(prev => prev.filter(invite => invite.invite_id !== inviteId));
    }, []);
    const currentUid = currentUser?.uid;
    const currentEmail = currentUser?.email;
    const currentPhoneNumber = currentUser?.phone_number;

    // Live list of the user's pending invites (Manage invitations). If the listener cannot
    // run or fails, e.g. the Firestore rules are not deployed yet, fall back to the API.
    useEffect(() => {
        if (!currentUid) return undefined;

        const stopWatching = watchPendingInvites(
            { email: currentEmail, phone_number: currentPhoneNumber },
            {
                onChange: setUserInvites,
                onError: error => {
                    console.error('Error watching invites, loading them once:', error);
                    refreshInvites();
                }
            }
        );

        if (!stopWatching) {
            refreshInvites();

            return undefined;
        }

        return stopWatching;
    }, [currentUid, currentEmail, currentPhoneNumber, refreshInvites]);
    useEffect(() => {
        (async () => {
            const firebaseAccessToken = AuthHelper.getFirebaseAccessToken();

            if (firebaseAccessToken) {
                axios.defaults.headers.common.Authorization = `Bearer ${firebaseAccessToken}`;
                await fetchCurrentUser();
            } else {
                setCurrentUser(null);
                setOrgs([]);
                setUserProperties([]);
                setCurrentAccessKeys([]);
                setUserInvites([]);

                setLoading(false);
            }
        })();
    }, [fetchCurrentUser]);

    const accessSummary = useMemo(
        () =>
            buildResiAccessSummary({
                user: currentUser,
                orgs,
                userProperties
            }),
        [currentUser, orgs, userProperties]
    );

    if (loading) {
        return <></>;
    }

    return (
        <AuthContext.Provider
            value={{
                ...authUser,

                user: currentUser,

                orgs,
                userProperties,
                properties: userProperties,
                userInvites,
                refreshInvites,
                removeInvite,
                accessSummary,

                accessKeysLoading,
                currentAccessKeys,
                refreshAccessKeys,
                
                refreshUser: fetchCurrentUser,

                login,
                resetPassword,
                logout
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

AuthProvider.propTypes = {
    children: PropTypes.node,
    authUser: PropTypes.object
};

const mapStateToProps = ({ auth }) => {
    const { authUser } = auth;
    return { authUser };
};

export default connect(mapStateToProps, {})(AuthProvider);

export function useAuth() {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
}
